import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Hangouts";

export default function MessagesThread() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useSafeBack("/lineage");
  const [msgs, setMsgs] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef();
  const listRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/${userId}`);
      setMsgs(data.messages);
      setOther(data.other);
      window.dispatchEvent(new Event("fv-refresh-badge"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => {
    document.body.classList.add("hide-bottom-nav");
    return () => document.body.classList.remove("hide-bottom-nav");
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    const h = (e) => { if (e.detail && (e.detail.sender_id === userId || e.detail.recipient_id === userId)) load(); };
    window.addEventListener("fv-chat", h);
    return () => { clearInterval(t); window.removeEventListener("fv-chat", h); };
  }, [load, userId]);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    const body = text.trim();
    setText("");
    try {
      const { data } = await api.post(`/messages/${userId}`, { text: body });
      setMsgs((m) => [...m, data]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>
      <div className="sticky top-0 z-40 glass hairline px-4 py-3 flex items-center gap-3">
        <button onClick={goBack} data-testid="msg-back-btn"><ArrowLeft size={22} /></button>
        <button onClick={() => other && navigate(`/u/${userId}`)} data-testid="msg-header-profile"
          className="flex items-center gap-3 min-w-0">
          <Avatar name={other?.name} sm />
          <h1 className="font-semibold text-base truncate">{other?.name || "Chat"}</h1>
        </button>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-2">
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div>
        ) : msgs.length === 0 ? (
          <p className="text-center text-sm py-10" style={{ color: "var(--text-muted)" }}>Say hello 👋</p>
        ) : msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div className="max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm"
                style={mine ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-primary)" }}>
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="glass p-3 flex items-center gap-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 12px)" }}>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message…" data-testid="msg-input"
          className="flex-1 rounded-full px-4 py-3 text-sm outline-none"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
        <button onClick={send} data-testid="msg-send-btn" className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}
